import { useCallback, useEffect, useMemo, useState } from "react";
import { getIRLMatchExcludedFolders, getIRLRequests } from "../store/irlStore";
import type { IRLItem, MatchResult } from "./types";
import {
  classifyFiles,
  hashFile,
  hashIrlList,
  readCachedMatch,
} from "./irlMatchClient";

/**
 * React hook for IRL matching inside FilePreviewTable.
 *
 * Manual-trigger by design: classification only fires when `classify()` is
 * called (i.e., when the user clicks "Run IRL match"). On mount, the hook
 * still walks the cache so files that were classified previously surface
 * their matches immediately without an API call.
 *
 * `folderCodes` is a parallel array to `files` — each entry is the file's
 * top-level folder code (e.g., "1.2.3" -> "1") or null for root. Combined
 * with the per-project excluded list (managed in the IRL tab), files in
 * excluded folders are skipped — no preview extraction, no API call.
 */
export function useIrlMatches(args: {
  projectId: string | undefined;
  files: File[];
  folderCodes?: (string | null)[];
}) {
  const { projectId, files, folderCodes } = args;

  // Read IRL list from localStorage (project-scoped). Hookified so the
  // dependency array can react to project changes.
  const irlList: IRLItem[] = useMemo(() => {
    if (!projectId) return [];
    return getIRLRequests(projectId).map((r) => ({
      number: r.number,
      topic: r.topic,
      entityName: r.entityName,
      request: r.request,
    }));
  }, [projectId]);

  const [matches, setMatches] = useState<Record<string, MatchResult>>({}); // keyed by file name
  const [classifying, setClassifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On files / irlList change, hydrate from cache only — never auto-call API.
  useEffect(() => {
    let cancelled = false;
    if (!projectId || irlList.length === 0 || files.length === 0) {
      setMatches({});
      return;
    }
    (async () => {
      try {
        const version = await hashIrlList(irlList);
        const next: Record<string, MatchResult> = {};
        for (const f of files) {
          const hash = await hashFile(f);
          const cached = await readCachedMatch(projectId, version, hash);
          if (cached) next[f.name] = cached;
        }
        if (!cancelled) setMatches(next);
      } catch (err) {
        console.warn("[irl-match] cache hydrate failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, files, irlList]);

  const classify = useCallback(async () => {
    if (!projectId) return;
    if (irlList.length === 0) {
      setError("No IRL list uploaded for this project. Upload one in the IRL tab first.");
      return;
    }
    if (files.length === 0) return;

    setClassifying(true);
    setError(null);
    try {
      // Read the excluded list at click time so toggling folders in the IRL
      // tab takes effect on the next run without remounting this hook.
      const excludedFolders = getIRLMatchExcludedFolders(projectId);
      const results = await classifyFiles({
        projectId,
        irlList,
        files,
        folderCodes,
        excludedFolders,
      });
      // If every result is "folder excluded", the user hasn't opted any folder
      // into matching — surface one clear error instead of polluting every row
      // with the same per-file reasoning (which makes a fresh project look
      // broken).
      const allExcluded =
        results.length > 0 && results.every((r) => r.reasoning === "folder excluded");
      if (allExcluded) {
        setError(
          "No folders selected for matching. Pick the folders you want analyzed in the IRL tab first.",
        );
        return;
      }
      // Merge with prior matches so excluded files (which classify skips) keep
      // any cached result they previously had.
      setMatches((prev) => {
        const next = { ...prev };
        for (const r of results) next[r.fileName] = r;
        return next;
      });
    } catch (err: any) {
      setError(err?.message || "Match failed");
    } finally {
      setClassifying(false);
    }
  }, [projectId, irlList, files, folderCodes]);

  const hasIrlList = irlList.length > 0;

  return { matches, classify, classifying, error, hasIrlList };
}
